import { FC, useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';

import cn from 'clsx';
import { useAuth } from '@hooks/Auth';
import { CompanyRole, Tier } from '@sdk/API';

const ActionIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path d="M4.1029 4L6.32103 6.66959H3.65783V13.795H6.32103V16.4646H1V4H4.1029Z" />
    <path d="M14.8716 4L11.651 7.89688L14.8573 11.7902H11.4048L9.92427 9.99851L8.44372 11.7902H4.99122L8.19311 7.89688L4.97784 4H8.43481L9.91892 5.8051L11.4048 4H14.8716Z" />
    <path d="M18.8494 16.4476H15.685L13.5284 16.4646V13.795H16.1862V6.66959H13.5284L15.7465 4H18.8494V16.4476Z" />
  </svg>
);

export type BottomAppBarItem = {
  active?: boolean;
  disabled?: boolean;
  icon?: string | JSX.Element;
  label: string | JSX.Element;
  value?: string;
  path?: string;
  unstyledIcon?: boolean;
};

type Props = {
  active?: string;
  items?: BottomAppBarItem[];
};

const AppBarItem: FC<BottomAppBarItem> = ({
  active,
  icon,
  label,
  path,
  disabled,
  unstyledIcon,
}) => {
  const rootCn = cn(
    'group w-full h-full justify-center inline-block text-center py-1 cursor-pointer',
    {
      'opacity-60 cursor-not-allowed': disabled,
      'text-cyan': active,
      'hover:text-gray-200': !active && !disabled,
      'fill-current focus:text-gray-200': !disabled,
    }
  );

  const iconCn = cn('flex justify-center mb-1 h-5', {
    'opacity-80 group-hover:opacity-90': !disabled,
    'opacity-100': active,
  });

  const labelCn = cn('font-display text-xs');

  const content = (
    <>
      {unstyledIcon ? icon : <div className={iconCn}>{icon}</div>}
      {typeof label === 'string' ? (
        <div className={labelCn}>{label}</div>
      ) : (
        label
      )}
    </>
  );

  if (!path || disabled)
    return (
      <div role="button" className={rootCn}>
        {content}
      </div>
    );

  return (
    <Link to={path} className={rootCn}>
      {content}
    </Link>
  );
};

const BottomAppbar: FC<Props> = ({ active, items }) => {
  const { dbUser, company } = useAuth();
  const location = useLocation();
  const [selected, setSelected] = useState<string>();

  useEffect(() => {
    if (selected === undefined) setSelected(active);
  }, [active, selected]);

  useEffect(() => {
    if (location.pathname.startsWith('/recent')) setSelected('recent');
    else if (location.pathname.startsWith('/feed')) setSelected('feeds');
    else if (location.pathname.startsWith('/alerts'))
      setSelected('notifications');
    else if (location.pathname.startsWith('/account')) setSelected('account');
    else if (location.pathname.startsWith('/action')) setSelected('action');
    else if (location.pathname.startsWith('/admin/actions'))
      setSelected('action');
    else if (location.pathname.startsWith('/admin/dashboard'))
      setSelected('dashboard');
    else if (location.pathname.startsWith('/home')) setSelected('home');
    else if (location.pathname.startsWith('/idealist')) setSelected('idea');
  }, [location.pathname, setSelected]);

  let isCompanyAdmin =
    company &&
    (company.role === CompanyRole.ADMIN ||
      company.role === CompanyRole.REPRESENTATIVE);

  return (
    <nav className="fixed inset-x-0 bottom-0 z-10 flex items-center justify-center py-2    text-gray-400 bg-gray-900 shadow">
      <div
        className="container grid items-start w-full px-1 mx-auto"
        style={{
          columnGap: 10,
          wordBreak: 'normal',
          gridTemplateColumns: 'repeat(6, 1fr)',
        }}
      >
        {items?.slice(0, 2).map((item) => (
          <AppBarItem
            key={`bottom-appbar-item-${item.value}`}
            active={selected === item.value}
            {...item}
          />
        ))}
        <div className="relative">
          <AppBarItem
            key="bottom-appbar-item-action"
            active={selected === 'action'}
            path={isCompanyAdmin ? '/admin/actions' : '/action'}
            label={<div className="pt-6 text-sm">Actions</div>}
            unstyledIcon
            icon={
              <div
                className="absolute inset-x-0 mx-auto text-center"
                style={{
                  color: '#5d5d61',
                  top: 0,
                  width: '1.8rem',
                  height: '1.8rem',
                }}
              >
                <ActionIcon
                  className=" group-hover:opacity-100"
                  style={{ height: '100%', width: '100%' }}
                />
              </div>
            }
          />
        </div>

        {(items?.length || 0) > 2 &&
          items
            ?.slice(2)
            .map((item) => (
              <AppBarItem
                key={`bottom-appbar-item-${item.value}`}
                active={selected === item.value}
                {...item}
              />
            ))}
      </div>
    </nav>
  );
};

export default BottomAppbar;
